/**
 * Branded id-typer for import-resolusjonen (WI 2318, design-of-record §2).
 *
 * FE hadde ingen id-branding fra før. To id-er forveksles lett fordi
 * begge er GUID-strenger over wire:
 *   - `ContainerId` — den opplastede arbeidsboka (Excel/Zip). **FE holder kun denne.**
 *   - `DatasetId`   — blob-GUID på det BE-resolverte arket. **BE eier den.**
 *
 * Brandingen er REN compile-time: `unique symbol`-feltet finnes aldri i runtime,
 * så en `ContainerId`/`DatasetId` ER strengen sin på verdi-nivå (cast-funksjonene
 * er no-ops). TypeScript nekter likevel å la den ene degradere til den andre
 * eller til rå `string` — som er hele poenget: «5 vs 14»-bug-en (feil ark)
 * kunne aldri oppstått hvis de to id-ene var uforvekslelige fra start.
 *
 * `as*`-funksjonene under er den ENESTE cast-stien (et grep for
 * `as ContainerId|as DatasetId` skal kun treffe denne fila → trivielt for
 * 2311's no-new-ref-lint). Bruk dem på API-grensen; resten av koden flyter
 * branded.
 */

declare const brand: unique symbol;

/** Påheft en kompileringstids-brand `B` på basis-typen `T` uten runtime-kost. */
export type Brand<T, B> = T & { readonly [brand]: B };

/** Den opplastede arbeidsboka (Excel/Zip-container). FE holder kun denne. */
export type ContainerId = Brand<string, 'ContainerId'>;

/** Blob-GUID på det BE-resolverte data-arket. BE eier den; FE leser den. */
export type DatasetId = Brand<string, 'DatasetId'>;

/** Cast en rå streng til `ContainerId` (compile-time-brand; runtime no-op). */
export function asContainerId (value: string): ContainerId {
  return value as ContainerId;
}

/** Cast en rå streng til `DatasetId` (compile-time-brand; runtime no-op). */
export function asDatasetId (value: string): DatasetId {
  return value as DatasetId;
}

/** Som `asContainerId`, men slipper null/undefined gjennom som `null`. */
export function asContainerIdOrNull (value: string | null | undefined): ContainerId | null {
  return value == null ? null : (value as ContainerId);
}

/** Som `asDatasetId`, men slipper null/undefined gjennom som `null`. */
export function asDatasetIdOrNull (value: string | null | undefined): DatasetId | null {
  return value == null ? null : (value as DatasetId);
}
